
// Chakra imports
import {
  Badge,
  Box,
  Button,
  Flex,
  Image,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  Heading,
  useBreakpointValue,
} from '@chakra-ui/react';
// Assets
// Metallurgy logo image (placed in public or assets/img directory)
import metallurgyLogo from 'assets/img/others/metallurgy-logo.png';
import necterelogo from 'assets/img/others/necterelogo.png';
import calimasterimage from 'assets/img/others/calimasterimage.jpeg';
// Custom components
import PricingLayout from 'layouts/auth/variants/Pricing';

function LandingPage() {
  const textColor = useColorModeValue('white', 'white');
  const highlightColor = useColorModeValue('blue.400', 'orange.300');
  const cardBg = useColorModeValue('whiteAlpha.100', 'whiteAlpha.100');
  const sectionBg = useColorModeValue('whiteAlpha.50', 'whiteAlpha.50');
  const headingSize = useBreakpointValue({ base: '2xl', md: '4xl' });

  return (
    <PricingLayout contentTop={{ base: '0', md: '0' }} contentBottom={{ base: '0', lg: '0' }}>
      <Box bg="#0B0F19" minH="100vh" color={textColor} position="relative" overflow="hidden">

        {/* Glow background */}
        <Box position="absolute" width="600px" height="600px" bgGradient="radial(#7C3AED55, transparent)" filter="blur(120px)" top="-100px" left="-100px" zIndex={0} />
        {/* NAVBAR */}
        {/* <Flex className="navbar" justify="space-between" align="center" px={{ base: 4, md: 10 }} py={8} position="relative" zIndex={1}>
          <Text fontWeight="bold" fontSize="xl">Calibration Master</Text>
          <Flex gap={6} align="center">
            <Text color="gray.400" cursor="pointer">Features</Text>
            <Text color="gray.400" cursor="pointer">Platform</Text>
            <Text color="gray.400" cursor="pointer">Pricing</Text>
            <Button bgGradient="linear(135deg, #7C3AED, #2563EB)" color="white" borderRadius="lg" px={6} py={2} boxShadow="0 0 20px #7C3AED55">Request Demo</Button>
          </Flex>
        </Flex> */}

        {/* HERO */}
        <Box className="hero" textAlign="center" pt={{ base: 16, md: 32 }} pb={16} position="relative" zIndex={1}>
          <Heading as="h1" size={headingSize} fontWeight="extrabold" lineHeight="1.2">
            Modern Calibration Management<br />for Enterprise Teams
          </Heading>
          <Flex justify="center" gap={4} mt={8} className="hero-buttons">
            <Button bgGradient="linear(135deg, #7C3AED, #2563EB)" color="white" borderRadius="lg" px={6} py={2} boxShadow="0 0 20px #7C3AED55">Request Demo</Button>
            <Button variant="outline" borderColor="whiteAlpha.300" color="white" borderRadius="lg" px={6} py={2}>View Platform</Button>
          </Flex>
          {/* HERO VISUAL */}
          <Box mt={16} mx="auto" maxW="520px" bg={cardBg} borderRadius="2xl" p={8} boxShadow="0 20px 60px rgba(0,0,0,0.5)" border="1px solid" borderColor="whiteAlpha.200">
            <Image src={calimasterimage} alt="calimaster Logo" boxSize={{ base: '220px', md: '320px' }} objectFit="contain" mx="auto" mt={4} />
          </Box>
        </Box>

        {/* FEATURES SECTION */}
        <Box className="section" py={24} bg={sectionBg}>
          <Box maxW="1200px" mx="auto" px={4}>
            <Heading as="h2" size="xl" textAlign="center" mb={12}>Complete Calibration Ecosystem</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              <FeatureCard badge="Assets" title="Asset Lifecycle Management" desc="Track every device with full calibration history and status." />
              <FeatureCard badge="Workflow" title="Calibration Scheduling" desc="Automate recurring calibration cycles and reminders." />
              <FeatureCard badge="Vendors" title="Vendor & SLA Management" desc="Assign vendors, track performance, and manage SLAs." />
              <FeatureCard badge="Jobs" title="Calibration Appointments" desc="Create and track calibration jobs with real-time updates." />
              <FeatureCard badge="Compliance" title="Audit & Compliance" desc="Maintain audit trails and meet regulatory requirements." />
              <FeatureCard badge="Certificates" title="Certificate Management" desc="Store calibration certificates linked to assets." />
            </SimpleGrid>
          </Box>
        </Box>

        {/* PLATFORM SECTION */}
        {/* <Box className="section" py={24}>
          <Box maxW="1200px" mx="auto" px={4}>
            <Heading as="h2" size="xl" textAlign="center" mb={12}>Built for Enterprise Scale</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              <FeatureCard title="Multi-Tenant Architecture" desc="Secure data separation across organizations." />
              <FeatureCard title="Role-Based Access" desc="Fine-grained permissions for all user types." />
              <FeatureCard title="Real-Time Notifications" desc="Stay updated with alerts and system events." />
              <FeatureCard title="API Integration" desc="Connect seamlessly with enterprise systems." />
              <FeatureCard title="Central Dashboard" desc="Monitor performance, overdue items, and insights." />
              <FeatureCard title="Secure Infrastructure" desc="Enterprise-grade cloud deployment and security." />
            </SimpleGrid>
          </Box>
        </Box> */}

        {/* CTA SECTION */}
        <Box className="cta" textAlign="center" py={22}>
          <Heading as="h2" size="2xl" mb={4}>Transform Your Calibration Operations</Heading>
          <Text color="gray.400" mb={8}>Improve efficiency, compliance, and visibility across your organization.</Text>
          <Button bgGradient="linear(135deg, #7C3AED, #2563EB)" color="white" borderRadius="lg" px={8} py={4} fontSize="lg">Schedule a Demo</Button>
        </Box>

        {/* FOOTER */}
        {/* <Box className="footer" textAlign="center" py={10} color="gray.400">
          © 2026 Nectere Tech Pvt. Ltd. | Calibration Master
        </Box> */}
      </Box>
    </PricingLayout>
  );
}

// FeatureCard component for reuse
function FeatureCard({ badge, title, desc }: { badge?: string; title: string; desc: string }) {
  return (
    <Box bg="rgba(255,255,255,0.05)" borderRadius="xl" p={8} border="1px solid" borderColor="whiteAlpha.200" boxShadow="md" _hover={{ transform: 'translateY(-6px)', borderColor: '#7C3AED' }} transition="0.3s">
      {badge && <Badge colorScheme="purple" bg="rgba(124,58,237,0.2)" color="#7C3AED" mb={2} fontSize="xs" borderRadius="full" px={3} py={1}>{badge}</Badge>}
      <Text fontWeight="bold" fontSize="xl" mb={2}>{title}</Text>
      <Text color="gray.400">{desc}</Text>
    </Box>
  );
}

export default LandingPage;
